/* import __COLOCATED_TEMPLATE__ from './collection-editor-modal-contents.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type MediaHelper } from 'embercom/objects/media/media-helper';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import ArticleGroup from 'embercom/models/articles/article-group';
import type RouterService from '@ember/routing/router-service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import ajax from 'embercom/lib/ajax';
import { EntityType } from 'embercom/models/data/entity-types';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    modal: any;
    title?: string;
    onNavigationBack?: () => void;
    onNavigationNext?: (viewId?: string) => void;
    showBackButton?: boolean;
    onModalClose: () => void;
    context: {
      localeId?: string;
      onCreate?: (collectionId: string) => void;
      selectedHc?: string;
      collection?: ArticleGroup;
      parentCollection?: ArticleGroup;
      inModalContext?: boolean;
      refreshData?: () => void;
      eventSource?: string;
      folder?: string;
    };
  };
}

export default class CollectionEditorModalContents extends Component<Signature> {
  @tracked collection: ArticleGroup;
  @tracked externalIconMediaHelper?: MediaHelper;
  @service declare appService: AppService;
  @service declare notificationsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare intl: $TSFixMe;
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare helpCenterService: $TSFixMe;

  constructor(owner: any, args: Signature['Args']) {
    super(owner, args);

    this.intercomEventService.trackAnalyticsEvent({
      action: this.args.context.parentCollection
        ? 'create_child_collection'
        : 'create_root_collection',
      object: 'collection_editor_modal',
      place: this.args.context.eventSource,
    });
    let helpCenterId = this.args.context.selectedHc || undefined;
    let parentCollection: ArticleGroup =
      this.args.context.parentCollection ?? this.store.peekRecord('articles/article-group', 'home');
    this.collection =
      this.args.context.collection ??
      ArticleGroup.createNewFolder(parentCollection, undefined, helpCenterId);

    if (!this.args.context.collection) {
      this.collection.name = '';
    }

    this.collection.initLocalizedContent();
  }

  get showIconSelector() {
    return !this.args.context.parentCollection;
  }

  get primaryButtonTranslationKey() {
    return this.args.context.collection
      ? 'knowledge-hub.content-editor.shared.collection-editor.save-collection'
      : 'knowledge-hub.content-editor.shared.collection-editor.create-collection';
  }

  get shouldDisableSaveCollectionButton() {
    return (
      !this.collection.selectedLocalizedContent?.name?.trim() ||
      !this.collection.selectedLocalizedContent?.description?.trim() ||
      !this.collection.icon
    );
  }

  @action
  async saveCollection() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'create_collection',
      object: 'collection_editor_modal',
      place: this.args.context.eventSource,
    });

    let collectionExternalIconUrl = this.collection.externalIconUrl;
    await this.collection.save();
    this.collection.externalIconUrl = collectionExternalIconUrl; // avoid blip in the icon preview while uploading

    try {
      let response = await this._saveExternalIconIfNeeded();
      if (response?.external_icon_url) {
        // makes sure the new external icon url is available to be used
        await this.collection.reload();
      }
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t('articles.collections.external-icon.upload.error'),
      );
      this.externalIconMediaHelper?.setProperties({
        isSaved: false,
        isReset: true,
      });
      return;
    }

    if (this.args.context.onCreate) {
      this.args.context.onCreate(this.collection.id);
    }

    if (this.args.context.refreshData) {
      this.args.context.refreshData();
    }

    if (
      // use 'startsWith' because the route can be the Sources page or the Contents page
      this.router.currentRouteName?.startsWith('apps.app.knowledge-hub') &&
      this.args.context.eventSource === 'knowledge_sources_select_content_modal'
    ) {
      this.knowledgeHubService.createContent(EntityType.ArticleContent, {
        folder_id: this.args.context.folder,
      });
      this.args.onModalClose();
    }

    this.args.onModalClose();
    this.helpCenterService.overrideSiteHasAtLeastOneCollection(true);
  }

  async _saveExternalIconIfNeeded() {
    if (this.externalIconMediaHelper) {
      let formData = new FormData();
      formData.append('asset_type', 'external_icon');
      formData.append('app_id', this.appService.app.id);
      formData.append('image', this.externalIconMediaHelper?.get('file'));
      let response = await ajax({
        type: 'POST',
        url: `/ember/article_folders/${this.collection.id}/upload_asset.json`,
        data: formData,
        contentType: false,
        processData: false,
      });

      return response;
    }
  }

  @action onNavigationBack() {
    if (this.args.onNavigationBack) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'navigate_back',
        object: 'collection_editor_modal',
        place: this.args.context.eventSource,
      });
      this.args.onNavigationBack?.();
    } else {
      this.closeModal();
    }
  }

  @action closeModal() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'canceled',
      object: 'collection_editor_modal',
      place: this.args.context.eventSource,
    });
    this.args.onModalClose();
  }

  willDestroy(): void {
    this.collection.rollbackAttributes(); // making sure we clean the changes done inside the modal and not saved
    super.willDestroy();
  }

  @action onSelectIcon(mediaHelper: MediaHelper) {
    if (mediaHelper) {
      this.collection.externalIconUrl = mediaHelper.get('isReset') ? '' : mediaHelper.get('url'); // This is used to show the correct preview
      if (!this.externalIconMediaHelper) {
        this.externalIconMediaHelper = mediaHelper;
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::Shared::CollectionEditorModalContents': typeof CollectionEditorModalContents;
    'knowledge-hub/content-editor/shared/collection-editor-modal-contents': typeof CollectionEditorModalContents;
  }
}
